import { createContext, useState, useContext, useEffect, useCallback } from "react";
import Cookies from 'js-cookie';
import { adApiRequest } from "../../../api/services/ad";

const AuctionsContext = createContext();

export const AuctionsProvider = ({children}) =>{
    const [step, setStep] = useState(1);
    const [adData, setAdData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [draftSaved, setDraftSaved] = useState(false);
    const [textChanged, setTextChanged] = useState('');
    const [errorModal, setErrorModal] = useState(false);
    const [errorModalText, setErrorModalText] = useState('');

    const currentUser = Window.user_data.loggedInUserID;
    const ad_id = Cookies.get(`ad_id_${currentUser}`)

     const handleServerError = (status, text500, text400) => {
        if (status >= 500) {
            setErrorModalText(text500);
        } else if (status >= 400) {
            setErrorModalText(text400);
        }
        setErrorModal(true);
    };


    useEffect(() => {
        const loadData = async () => {
            const response = await adApiRequest('GET',ad_id);
            if (response.ok) {
                setAdData(prev => ({ adLayers: [], markers: [], ...response.data }));
            } else {
                handleServerError(
                    response.status,
                    'Försök igen senare eller kontakta Kundservice.',
                    'Den här funktionen är inte tillgänglig.'
                );
            }
        };

        loadData();
    }, []);

    const getAdFiles = async() => {
       const response = await adApiRequest('GET',ad_id);
       if (response.ok){
        setAdData(prev => ({...prev, attachments:response.data.attachments}))
       }else{
        handleServerError(response.status,'Försök igen senare eller kontakta Kundservice.',  'Den här funktionen är inte tillgänglig.')
       }
    }

    const adLayersToGeoJson = (adLayers) => {
        let group = new L.FeatureGroup();
        adLayers.forEach((itm, i) => {
            itm.layer.addTo(group)
        })

        return group.toGeoJSON()
    }

    const updateAdData = useCallback((changed) => {
        let data = { ...changed };
        if (changed.hasOwnProperty('adLayers')) {
            data["custom_area"] = adLayersToGeoJson(data["adLayers"]);
            let sections = [];
            data["adLayers"].forEach((adLayer) => {
                if (adLayer.data) {
                    const tgl = {
                        "Tall": adLayer.data.tall / 100,
                        "Gran": adLayer.data.gran / 100,
                        "default": adLayer.data.lov / 100,
                    };

                    if (adLayer.data.al !== null && adLayer.data.al !== '' && !isNaN(adLayer.data.al)) tgl["Al"] = adLayer.data.al / 100;
                    if (adLayer.data.asp !== null && adLayer.data.asp !== '' && !isNaN(adLayer.data.asp)) tgl["Asp"] = adLayer.data.asp / 100;
                    if (adLayer.data.björk !== null && adLayer.data.björk !== '' && !isNaN(adLayer.data.björk)) tgl["Björk"] = adLayer.data.björk / 100;
                    if (adLayer.data.bok !== null && adLayer.data.bok !== '' && !isNaN(adLayer.data.bok)) tgl["Bok"] = adLayer.data.bok / 100;
                    if (adLayer.data.ek !== null && adLayer.data.ek !== '' && !isNaN(adLayer.data.ek)) tgl["Ek"] = adLayer.data.ek / 100;


                    sections.push({
                        "Name": (adLayer.layer._leaflet_id).toString(),
                        "totalVolume": adData.arrangement == "gallring" ? Math.trunc(adLayer.data.volume * 0.3) : Math.trunc(adLayer.data.volume),
                        "Hectares": adLayer.data.area.toFixed(2),
                        "HabitatIndex": adData.habitat_index,
                        "TGL": tgl
                    });
                }
            })
            data["forestWithSections"] = { sections }
            delete data["adLayers"];
        }
        const loadUpdatedData = async () => {
            let response = await adApiRequest('PATCH',ad_id, data)
            if (response.ok) {
                setAdData(prev => ({ ...prev, ...response.data }))    
            }

        }
        loadUpdatedData();
    }, [ adData])

    const changeAdData = useCallback((changed)=>{
        if (Object.keys(changed).includes('comments')){
            setTextChanged(changed.comments);
        }else{
            updateAdData(changed);
            setAdData(prev=>({...prev, ...changed}));           
        }
    },[ updateAdData])

    const saveDraft = () =>{
        changeAdData({visible_draft:true});
        setDraftSaved(true);
        setTimeout(()=>{setDraftSaved(false)},5000);
    }

    const handleFinish = () =>{
        setLoading(true);
        updateAdData({comments: textChanged,state:20})
        window.location.href = `/auctions/${adData.id}/review`;
    }

    // Steps valid
    const isStep1Valid = () => {
        return adData &&
            adData.type !== null &&
            adData.geoCollection &&
            adData.geoCollection.geometries.length !== 0 &&
            adData.start_time &&
            adData.end_time &&
            adData.felling_start_time &&
            adData.felling_end_time &&
            (adData.arrangement !== "gallring" ? adData.felling_application_responsible !== null : true);
    }

    const isStep2Valid = () =>{
        return adData && adData.forestWithSections &&
            adData.forestWithSections.sections &&
            adData.forestWithSections.sections.length !== 0;
    } 

    return(
        <AuctionsContext.Provider value={{ 
            step, setStep,
            adData, changeAdData, getAdFiles,
            isStep1Valid, isStep2Valid,
            saveDraft, draftSaved,
            handleFinish,
            errorModal, setErrorModal,
            errorModalText, setErrorModalText,
            handleServerError,
            handleBack: () => setStep(prev => prev - 1),
            loading, setLoading
            }}>
            {children}
        </AuctionsContext.Provider>
    )

}

export const useAuctions = () => useContext(AuctionsContext);
export default AuctionsContext;